<template>
  <div class="container mt-5">

    <!-- Logged-In State -->
    <div v-if="isLoggedIn">

      <h1 class="text-center mb-4">Welcome to HuntApply.com</h1>
      <div v-if="user && user.firstName" class="text-center">
        <p>Great to see you again, {{ user.firstName }}!</p>
      </div>

      <!-- Premium users: their saved draw points, or an invitation to add them -->
      <div v-if="hasActiveSubscription" class="mt-5">
        <h4 class="text-center mb-3">Your Draw Points</h4>
        <DrawPointsTable v-if="drawPoints && drawPoints.length > 0" :data="drawPoints"
          @row-click="handleDrawPointsRowClick" @edit-points="$emit('showManualPoints')" />
        <div v-else class="text-center">
          <p>You haven't added any Utah preference points yet.</p>
          <p>
            <a href="#" @click.prevent="$emit('showManualPoints')">Enter your points</a> to get started.
          </p>
        </div>
      </div>

      <!-- Non-premium users: DrawPointsTable with upgrade invitation -->
      <div v-else class="mt-5">
        <DrawPointsTable :data="[]" @row-click="handleDrawPointsRowClick" />
      </div>
  

      <!-- Draw Odds component -->
      <div class="mt-5" ref="drawOddsSection">
        <ViewDrawOdds 
          :drawPoints="drawPoints" 
          :selectedSpecies="selectedSpecies"
          :selectedPoints="selectedPoints"
          ref="viewDrawOdds"
        />
      </div>
    </div>

    <!-- Logged-Out State (hero-like marketing section) -->
    <div v-else>
      <!-- Hero Section -->
      <div class="hero-section text-center p-4 p-md-5 mb-4 rounded">
        <h1 class="display-4 fw-bold">Plan Your Hunts with Confidence</h1>

        <div class="mt-4">
          <div class="logo-container">
            <img
              src="../assets/Transparent-Background.png"
              alt="Logo"
              class="img-fluid logo-image mb-3"
            />
          </div>
        </div>

        <p>
          <a href="#" class="btn btn-primary btn-lg me-2" @click="$emit('showSignup')">Sign Up</a>
          <a href="#" class="btn btn-outline-primary btn-lg" @click="$emit('showSignin')">Sign In</a>
        </p>
        <p class="lead mb-4">
          Sign up for an account on HuntApply.com to access:
        </p>
        <div class="row justify-content-center">
          <div class="col-md-8">
            <ul class="list-unstyled fs-5 text-start mx-auto" style="max-width: 600px;">
              <li class="mb-3">
                <strong>Free Utah Draw Odds</strong> – Know your chances before you apply.
              </li>
              <li class="mb-3">
                <strong>Track Your Personal Draw Points</strong> – All in one place.
              </li>
              <li class="mb-3">
                <strong>Hunt Finder Map</strong> – Explore hunting units and boundaries.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ViewDrawOdds from './ViewDrawOdds.vue';
import DrawPointsTable from './DrawPointsTable.vue';
import MapLinks from './MapLinks.vue';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'Home',
  components: {
    ViewDrawOdds,
    DrawPointsTable,
    MapLinks,
  },
  data() {
    return {
      species: "",
      speciesList: ["Deer", "Elk", "Moose", "Bear"],
      drawOdds: null,
      selectedSpecies: "",
      selectedPoints: 0,
    };
  },
  computed: {
    ...mapState({
      user: state => state.user,
      drawPoints: state => state.drawPoints,
    }),
    ...mapGetters({
      isLoggedIn: 'isAuthenticated',
      hasActiveSubscription: 'hasActiveSubscription'
    }),
  },
  created() {
  //   document.title = "HuntApply | Utah Hunting Application & Draw Odds";
  // document.querySelector('meta[name="description"]').setAttribute("content", 
  //   "Track your Utah draw points, check hunting draw odds, and receive notifications for upcoming hunts. Plan your applications with HuntApply."
  // );
    if (this.isLoggedIn) {
      if (!this.user) {
        this.fetchUserData();
      }
      if (!this.drawPoints) {
        this.fetchDrawPoints();
      }
    }
  },
  methods: {
    ...mapActions(['setUser', 'setDrawPoints', 'logout']),

    async fetchUserData() {
      try {
        const response = await fetch(this.$API_BASE_URL + '/api/customers/whoami', {
          method: 'GET',
          credentials: "include",
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.setUser(data);
        } else {
          console.error('Failed to fetch user information');
          this.logout();
        }
      } catch (error) {
        console.error('Error:', error);
        this.logout();
      }
    },

    async fetchDrawPoints() {
      try {
        const response = await fetch(this.$API_BASE_URL + '/api/drawpoints', {
          method: 'GET',
          credentials: "include",
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          this.setDrawPoints(data.drawPointsList);
        }
      } catch (error) {
        console.error('Error fetching drawPoints:', error);
      }
    },

    handleDrawPointsRowClick(item) {
      this.selectedSpecies = item.species;
      this.selectedPoints = item.points;

      // Scroll to the draw odds section
      this.$nextTick(() => {
        this.$refs.drawOddsSection?.scrollIntoView({ behavior: 'smooth' });
      });
    },
  },
};
</script>

<style scoped>
/* Logo styling */
.logo-container {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
}

.logo-image {
  max-width: 100%;
  height: auto;
  max-height: 600px;
  object-fit: contain;
}

/* Example hero section styling */
.hero-section {
  background: linear-gradient(
    45deg,
    #ff9a9e 0%,
    #fad0c4 10%,
    #fad0c4 20%,
    #a1c4fd 60%,
    #c2e9fb 85%,
    #e5f5c0 100%
  );
  border: 1px solid #c6d9f1;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1;
}

.hero-section > * {
  position: relative;
  z-index: 2;
}

/* Make the hero responsive; large text on bigger screens */
@media (min-width: 768px) {
  .hero-section {
    text-align: center;
  }
  .hero-section .display-4 {
    font-size: 2.5rem;
  }
}

/* Basic styling for your list items */
.hero-section ul {
  line-height: 1.6;
}

/* Small screen only styling for map invitation */
.small-screen-only {
  display: none; /* Hidden by default on large screens */
}

/* Show on small screens (below 1280px) */
@media (max-width: 1279px) {
  .small-screen-only {
    display: block; /* Show on small screens */
  }
}

</style>
